
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WelcomeScreen from './steps/WelcomeScreen';
import ProfileCreation from './steps/ProfileCreation';
import ExpertiseSelection from './steps/ExpertiseSelection';
import MonetizationSetup from './steps/MonetizationSetup';
import EditorWalkthrough from './steps/EditorWalkthrough';
import ContentImport from './steps/ContentImport';
import QuickStartGuide from './steps/QuickStartGuide';
import ProfileCompletion from './steps/ProfileCompletion';
import SocialConnection from './steps/SocialConnection';
import { useToast } from '@/hooks/use-toast';

const JournalistOnboarding: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    expertise: [] as string[],
    topics: [] as string[],
    pricing: 'free',
    paymentDetails: {},
    importedContent: [] as any[],
    socialConnections: [] as string[],
  });
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const totalSteps = 9;
  
  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      // Complete onboarding
      toast({
        title: "Onboarding complete!",
        description: "Your journalist profile has been set up successfully.",
      });
      navigate('/dashboard');
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  
  const updateFormData = (data: Partial<typeof formData>) => {
    setFormData({
      ...formData,
      ...data,
    });
  };
  
  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeScreen onNext={handleNext} />;
      case 2:
        return (
          <ProfileCreation 
            formData={formData}
            updateFormData={updateFormData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 3:
        return (
          <ExpertiseSelection
            formData={formData}
            updateFormData={updateFormData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 4:
        return (
          <MonetizationSetup
            formData={formData}
            updateFormData={updateFormData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 5:
        return (
          <EditorWalkthrough
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 6:
        return (
          <ContentImport
            formData={formData}
            updateFormData={updateFormData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 7:
        return (
          <QuickStartGuide
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 8:
        return (
          <ProfileCompletion
            formData={formData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 9:
        return (
          <SocialConnection
            formData={formData}
            updateFormData={updateFormData}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      default:
        return null;
    }
  };
  
  return renderStep();
};

export default JournalistOnboarding;
